import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import './Login.css';

// Material UI components
import Container from '@mui/material/Container';
import Paper from '@mui/material/Paper';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import Divider from '@mui/material/Divider';
import Alert from '@mui/material/Alert';

const Login = () => {
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    rememberMe: false
  });
  
  const [errors, setErrors] = useState({});
  const [showInfoMessage, setShowInfoMessage] = useState(false);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({
      ...formData,
      [name]: type === 'checkbox' ? checked : value
    });
    
    // Clear error when user types
    if (errors[name]) {
      setErrors({
        ...errors,
        [name]: ''
      });
    }
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.email) {
      newErrors.email = 'Email is required';
    } else if (!/^\S+@\S+\.\S+$/.test(formData.email)) {
      newErrors.email = 'Email is invalid';
    }
    
    if (!formData.password) {
      newErrors.password = 'Password is required';
    } else if (formData.password.length < 6) {
      newErrors.password = 'Password must be at least 6 characters';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
  e.preventDefault();

  if (validateForm()) {
    console.log('Login form submitted with data:', formData);

    // retrigger the alert even if it was already shown
    setShowInfoMessage(false);
    requestAnimationFrame(() => setShowInfoMessage(true));
  } else {
    setShowInfoMessage(false);
  }
};

  const handleForgotPasswordClick = (e) => {
    e.preventDefault();
    setShowInfoMessage(false);
    requestAnimationFrame(() => setShowInfoMessage(true));
};

  return (
    <Container maxWidth="sm" className="login-container">
      <title>Sign In | iCredentialing Provider Portal</title>
      <meta name="description" content="Securely sign in to the iCredentialing provider portal to manage credentialing, onboarding, and compliance information."/>
      <link
          rel="canonical"
          href={`https://icredentialing.com/login`}
      />
      <meta name="robots" content="noindex, nofollow" />
      <Box
        sx={{
          width: '100%',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
        }}
      >
        {showInfoMessage && (
          <Box sx={{ width: '100%', maxWidth: 500, mb: 2 }}>
            <Alert severity="info">
              The user is not registered. Please get in touch with us by either filling out the{' '}
              <Link to="/contact" className="contactus-link">
                <strong>Contact Us</strong>
              </Link>{' '}
              form or{' '}
              <Link to="/schedule-demo" className="contactus-link">
                <strong>Schedule a Meet</strong>
              </Link>
              .
            </Alert>
          </Box>
        )}
        <Paper elevation={3} className="login-paper" sx={{ width: '100%', maxWidth: 500 }}>
          <Box textAlign="center" mb={4}>
            <Typography variant="h4" component="h1" className="login-title">
              Welcome Back
            </Typography>
            <Typography variant="subtitle1" color="textSecondary">
              Sign in to access your account
            </Typography>
          </Box>
          
          <Box component="form" onSubmit={handleSubmit}>
            <TextField
              fullWidth
              label="Email Address"
              name="email"
              type="email"
              variant="outlined"
              margin="normal"
              value={formData.email}
              onChange={handleChange}
              error={!!errors.email}
              helperText={errors.email}
              required
            />
            
            <TextField
              fullWidth
              label="Password"
              name="password"
              type="password"
              variant="outlined"
              margin="normal"
              value={formData.password}
              onChange={handleChange}
              error={!!errors.password}
              helperText={errors.password}
              required
            />
            
            <Grid container alignItems="center" justifyContent="space-between" sx={{ mt: 2, mb: 3 }}>
              <Grid item>
                <label className="remember-me">
                  <input
                    type="checkbox"
                    name="rememberMe"
                    checked={formData.rememberMe}
                    onChange={handleChange}
                  />
                  <span>Remember me</span>
                </label>
              </Grid>
              
              <Grid item>
                <Link
                  to="#"
                  className="forgot-password"
                  onClick={handleForgotPasswordClick}
                >
                  Forgot password?
                </Link>
              </Grid>
            </Grid>
            
            <Button
              type="submit"
              variant="contained"
              fullWidth
              sx={{
                py: 1.5,
                borderRadius: '90px',
                backgroundColor: '#2e7d98',
                '&:hover': { backgroundColor: '#1c6a85' },
                fontSize: '1rem',
                mb: 2
              }}
            >
              <strong>Sign In</strong>
            </Button>
          </Box>
          
          <Divider sx={{ my: 3 }}>
            <Typography variant="body2" color="textSecondary">
              OR
            </Typography>
          </Divider>
          
          <Box textAlign="center" mt={2}>
            <Typography variant="body2">
              Don't have an account?{' '}
              <Link to="/contact" className="contactus-link">
                <strong>Contact us</strong>
              </Link>
              {' '}for creating the accouunt
            </Typography>
          </Box>
        </Paper>
      </Box>
    </Container>
  );
};

export default Login;